import { useEffect, type ReactElement } from "react";
import { logger } from "@-/dev.improve.haniverse.lib.logger";

export interface ScreenReadyMarkerProps {
  /**
   * Extra diagnostic attributes. Keep this object stable for the marker's lifetime.
   */
  params?: Readonly<Record<string, unknown>>;
  /**
   * Stable, human-readable name for the screen (e.g. `"browse"`).
   */
  screen: string;
}

/**
 * Lightweight, local-only screen-ready marker: a time-to-content (TTI) mark for a destination,
 * recorded once its visible frame has committed. The former native Observe/AppMetrics integration
 * installed URL-session/OkHttp instrumentation and network-path monitoring during process launch,
 * before React existed. That violates the boot contract, so readiness has no native SDK,
 * persistence, dispatch, or connectivity work.
 *
 * This marker no longer releases the native boot shell — `ChromeReadyMarker` does. A screen sits
 * behind its own suspense boundary and may never commit at all (an offline device is a legitimate
 * reason for content to suspend forever), so it cannot gate a native surface. Keeping this mark
 * behind the data is the point: it measures when content actually arrived.
 *
 * Renders nothing.
 */
export function ScreenReadyMarker({ params, screen }: ScreenReadyMarkerProps): ReactElement {
  useEffect(() => {
    if (__DEV__) {
      logger(import.meta.location).info("screen-ready", {
        elapsedSinceJsStartMs: Math.round(performance.now()),
        params,
        screen,
      });
    }
  }, [params, screen]);

  return <></>;
}
